import type { Params } from "react-router-dom";

/**
 * The parts of a React Router match used to recover its route pattern.
 */
export type RoutePatternMatch = {
    pathname: string;
    params: Params;
};

/**
 * Decodes a URL segment for comparison with router parameters, which React Router decodes.
 *
 * Postconditions: Malformed escape sequences are compared as written.
 */
const decodeSegment = (segment: string) => {
    try {
        return decodeURIComponent(segment);
    } catch {
        return segment;
    }
};

/**
 * Rebuilds the route pattern, such as `/trials/:trialId/details`, for a matched route branch.
 *
 * Preconditions: Matches are ordered from the root, as returned by React Router's useMatches.
 * Every match carries the branch's complete parameters, so only the deepest match is used.
 * Postconditions: Segments are read left to right; a segment becomes `:name` when it equals the
 * value of a named parameter not yet used, and the trailing segments a splat consumed become a
 * single `*`. A `create` segment is always kept literally, even when it fills an ID parameter, so
 * create pages have their own pattern. Other segments are kept literally. The result has a leading
 * slash and no trailing slash; an empty branch yields `/`.
 * Assumptions: A literal segment equal to a later parameter's value would be taken for that
 * parameter. Application parameters are record identifiers, so this does not arise in practice.
 */
const getRoutePattern = (matches: readonly RoutePatternMatch[]) => {
    const leaf = matches[matches.length - 1];
    if (!leaf) {
        return "/";
    }

    const segments = leaf.pathname.split("/").filter(Boolean);
    const splatLength = leaf.params["*"]?.split("/").filter(Boolean).length ?? 0;
    const unused = Object.entries(leaf.params).filter(
        (entry): entry is [string, string] => entry[0] !== "*" && entry[1] !== undefined
    );
    const pattern = segments.slice(0, segments.length - splatLength).map(segment => {
        const decoded = decodeSegment(segment);
        // Pages create a record when their ID parameter is "create" (see parseRouteId), so a create
        // page keeps a key of its own rather than sharing the edit page's.
        if (decoded.toLowerCase() === "create") {
            return "create";
        }

        const index = unused.findIndex(([, value]) => value === decoded);
        if (index < 0) {
            return segment;
        }

        const [name] = unused.splice(index, 1)[0];
        return `:${name}`;
    });

    if (splatLength > 0) {
        pattern.push("*");
    }

    return `/${pattern.join("/")}`;
};

export default getRoutePattern;
